import { Button, Dropdown } from "antd";
import { FileText, Map, ChevronDown } from "lucide-react";
import "./ViewerLayout.css";
import { useTopic } from "./hooks/use-topics";
import { ViewSelector } from "components/ViewSelector/ViewSelector";
import { MemberMarker, VojomoLoader } from "@vojomo-core/shared";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  apiRequest,
  Topic,
  useAccount,
  useGlobal,
  useRoadMap
} from "@vojomo-core/shared";

type BuildingBlocksDictionary = {
  my: Record<string, Topic | "text">;
  team: Record<string, Topic | "text">;
};

export default function ViewerLayout({
  inModalView,
  children
}: {
  inModalView: boolean;
  children: React.ReactNode;
}) {
  const { companyName } = useAccount();
  const {
    topicType,
    topicTitle,
    topic,
    setActiveTab,
    setRoadmapViewMode,
    hasReadAccess
  } = useTopic();
  const { view } = useGlobal();
  const { setActiveBlock } = useRoadMap();
  const navigate = useNavigate();

  const [recentGeneralDocuments, setRecentGeneralDocuments] = useState<Topic[]>(
    []
  );

  const [roadmapDocuments, setRoadmapDocuments] =
    useState<BuildingBlocksDictionary>(null);

  const pageTitle = useMemo(() => {
    switch (topicType) {
      case "building-block":
        return "Strategic Roadmap";
      case "briefing-doc":
        return "Alignment Document";
      default:
        return "General Documents";
    }
  }, [topicType]);

  useEffect(() => {
    const f = async () => {
      const data = await apiRequest<BuildingBlocksDictionary>({
        method: "GET",
        subRoute: "roadmap",
        path: "/get_all_building_blocks"
      });

      setRoadmapDocuments(data);
    };

    f();
  }, [companyName]);

  useEffect(() => {
    const f = async () => {
      const data = await apiRequest<Topic[]>({
        method: "GET",
        subRoute: "topic",
        path: "/latest_general_documents"
      });

      setRecentGeneralDocuments(data);
    };

    f();
  }, []);

  useEffect(() => {
    if (
      roadmapDocuments &&
      topic &&
      topic.type === "building-block" &&
      !inModalView
    ) {
      navigate(
        `/documents/docs/${roadmapDocuments[view][topic["title"]]["_id"]}`
      );
    }
  }, [view, roadmapDocuments, topic]);

  useEffect(() => {
    if (topic) {
      if (topic.type === "building-block" || topic.type === "briefing-doc") {
        setActiveBlock(topic["title"]);
      } else {
        setActiveBlock(null);
      }
    }
  }, [topic]);

  if (hasReadAccess === false) {
    return (
      <div className="flex justify-center items-center h-[90vh]">
        <p className="text-center font-bold">
          You don't have permissions to view this!
        </p>
      </div>
    );
  }

  const roadmapBbTitles = [
    "Vision",
    "Mission",
    "Peak Ambition",
    "Peak Ambition Objectives",
    "3 Year Objectives",
    "1 Year Objectives",
    "Quarterly Objectives"
  ];

  if (topicType === null) {
    return <VojomoLoader />;
  }

  if (inModalView) {
    return children;
  }

  return (
    <div className="app-shell">
      <header className="layout-header">
        <div className="layout-header-inner">
          <div className="layout-title-group">
            <div className="layout-title-row">
              <h1 className="layout-title">{pageTitle}</h1>

              {topicType !== "building-block" &&
                topicType !== "briefing-doc" &&
                recentGeneralDocuments.length > 0 && (
                  <Dropdown
                    menu={{
                      onClick: ({ key }) => {
                        if (key === "documents_list") {
                          navigate("/documents/docs");
                          return;
                        }
                        navigate(`/documents/docs/${key}`);
                      },
                      items: [
                        {
                          key: "documents_list",
                          label: (
                            <div className="layout-menu-item">
                              <FileText className="layout-menu-icon" />
                              <span>Documents List</span>
                            </div>
                          )
                        },
                        {
                          key: "recent_documents_label",
                          label: (
                            <span className="layout-menu-label">
                              Recent Documents
                            </span>
                          ),
                          disabled: true,
                          className: "layout-menu-label-item"
                        },
                        ...recentGeneralDocuments.map((gd) => {
                          return {
                            key: gd["_id"],
                            label: gd.title || "Untitled"
                          };
                        })
                      ],
                      defaultValue: topicTitle
                    }}
                    overlayClassName="layout-docs-dropdown"
                    placement="bottomLeft"
                  >
                    <Button
                      type="text"
                      size="small"
                      className="layout-dropdown-trigger"
                    >
                      <ChevronDown className="layout-chevron" />
                    </Button>
                  </Dropdown>
                )}

              {topicType == "building-block" && roadmapDocuments && (
                <Dropdown
                  menu={{
                    onClick: ({ key }) => {
                      if (key === "roadmap_at_a_glance") {
                        setRoadmapViewMode("table");
                        setActiveTab("Roadmap");
                        return;
                      }
                      navigate(`/documents/docs/${key}`);
                    },
                    items: [
                      {
                        key: "roadmap_at_a_glance",
                        label: (
                          <div className="layout-menu-item">
                            <Map className="layout-menu-icon" />
                            <span>Roadmap at a glance</span>
                          </div>
                        )
                      },
                      {
                        key: "building_blocks_label",
                        label: (
                          <span className="layout-menu-label">
                            Building Blocks
                          </span>
                        ),
                        disabled: true,
                        className: "layout-menu-label-item"
                      },
                      ...roadmapBbTitles.map((bbName) => {
                        if (roadmapDocuments[view][bbName]) {
                          return {
                            key: roadmapDocuments[view][bbName]["_id"],
                            label: bbName
                          };
                        }
                      })
                    ]
                  }}
                  overlayClassName="layout-docs-dropdown"
                  placement="bottomLeft"
                >
                  <Button
                    type="text"
                    size="small"
                    className="layout-dropdown-trigger"
                  >
                    <ChevronDown className="layout-chevron" />
                  </Button>
                </Dropdown>
              )}

              <span className="layout-title-divider" aria-hidden="true" />
              <MemberMarker />
            </div>
          </div>

          <div className="layout-header-right">
            {topicType === "building-block" && <ViewSelector />}
          </div>
        </div>
      </header>

      <div className="layout-body">
        <main className="layout-main">{children}</main>
      </div>
    </div>
  );
}
